import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import UserSettingsService from "src/services/userSettingsService";
import { useDispatch, useSelector } from "src/store";

import { updateSearchQuery } from "@dashboard/devices/store";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { expandGroupIdsWithDescendants, getGroupsForAggregation } from "@dashboard/groups/utils";
import { calculateBarValue } from "@dashboard/overview/components/shared";
import { Theme, useTheme } from "@mui/material";
import { CustomTileText } from "@shared/CustomTile";
import { ProgressBar } from "@shared/ProgressBar";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { TextLink } from "@shared/TextLink";

function calculateBarColor(warningDevicesCount: number, total: number, theme: Theme) {
    if (!warningDevicesCount) {
        return theme.palette.emteria.success;
    }
    if (!total) {
        return theme.palette.emteria.border.primary;
    }
    return theme.palette.emteria.warning;
}

type Props = {
    groupIds: number[];
};

export const WarningCard = (props: Props) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation(["pages", "common"]);
    const theme = useTheme();

    const groups = useSelector(selectGroups);
    const { devicesCount, warningDevicesCount } = useMemo(() => {
        const groupsForAggregation = getGroupsForAggregation(props.groupIds, groups);
        const devicesTotal = groupsForAggregation.reduce((acc, group) => acc + (group.statistics?.deviceCount ?? 0), 0);
        const warningsTotal = groupsForAggregation.reduce(
            (acc, group) => acc + (group.statistics?.healthStatus?.unhealthy ?? 0),
            0,
        );
        return { devicesCount: devicesTotal, warningDevicesCount: warningsTotal };
    }, [props.groupIds, groups]);

    const barValue = calculateBarValue(warningDevicesCount, devicesCount);
    const barColor = calculateBarColor(warningDevicesCount, devicesCount, theme);

    const handleOnClick = (_e: React.MouseEvent<HTMLAnchorElement>) => {
        const value = warningDevicesCount ? ["Warning"] : ["OK"];
        const baseGroupIds = props.groupIds.length > 0 ? props.groupIds : groups.map((g) => g.id);
        const groupIdsForDeviceFilter = expandGroupIdsWithDescendants(baseGroupIds, groups);
        UserSettingsService.setWorkspaceValue("devicePage.columnFilters", [
            { id: "licenseStatus", value: ["Used"] },
            { id: "warnings", value },
            { id: "groupName", value: groupIdsForDeviceFilter.map((id) => id.toString()) },
        ]);
        dispatch(updateSearchQuery(""));
        navigate("/dashboard/devices");
    };

    return (
        <SectionPanel title={t("pages:overviewPage.cards.deviceWarnings.title")}>
            {!devicesCount ? <CustomTileText>{t("common:table.noDevices")}</CustomTileText> : null}
            {!warningDevicesCount && devicesCount ? (
                <CustomTileText>{t("pages:overviewPage.cards.deviceWarnings.noWarningDevices")}</CustomTileText>
            ) : null}
            {warningDevicesCount ? (
                <TextLink onClick={handleOnClick} tooltip={t("common:tooltip.showDevices", { count: warningDevicesCount })}>
                    {t("pages:overviewPage.cards.deviceWarnings.warningDevices", { count: warningDevicesCount })}
                </TextLink>
            ) : null}
            <ProgressBar progress={devicesCount ? barValue : 0} color={barColor} hideLabel={!devicesCount} />
        </SectionPanel>
    );
};
